import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Ticket, Clock, ArrowRight, Loader2, LogOut, User, Menu, X, LayoutDashboard, Timer, Trophy, CreditCard, Minus, Plus } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import LegalFooter from '../components/LegalFooter';
import { AgeTermsCheckbox } from '../components/AgeTermsCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Raffle images from design guidelines
const RAFFLE_IMAGES = [
  'https://images.unsplash.com/photo-1672024110512-f7028b49db28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBkYXJrfGVufDB8fHx8MTc3NTY0NzUyNHww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/94272/sports-car-pkw-auto-vehicle-94272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1707312900236-12d6fefd2bbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxnYW1pbmclMjBzZXR1cCUyMGRhcmt8ZW58MHx8fHwxNzc1NjQ3NTI0fDA&ixlib=rb-4.1.0&q=85'
];

function CountdownUnit({ value, label }) {
  const prevValue = useRef(value);
  const [flip, setFlip] = useState(false);

  useEffect(() => {
    if (prevValue.current !== value) {
      setFlip(true);
      const t = setTimeout(() => setFlip(false), 300);
      prevValue.current = value;
      return () => clearTimeout(t);
    }
  }, [value]);

  return (
    <div className="flex flex-col items-center" data-testid={`countdown-${label}`}>
      <div className={`relative bg-gray-900 text-white rounded-lg w-12 h-11 flex items-center justify-center text-lg font-bold font-mono shadow-md overflow-hidden transition-transform duration-300 ${flip ? 'scale-95' : 'scale-100'}`}>
        <span className={`transition-all duration-300 ${flip ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
          {String(value).padStart(2, '0')}
        </span>
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
      </div>
      <span className="text-[10px] uppercase tracking-wider text-gray-500 mt-1 font-medium">{label}</span>
    </div>
  );
}

function Countdown({ drawDate }) {
  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft(drawDate));

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(calcTimeLeft(drawDate)), 1000);
    return () => clearInterval(id);
  }, [drawDate]);

  if (timeLeft.expired) {
    return (
      <div className="flex items-center gap-1.5 text-[#EAB308] font-semibold text-sm" data-testid="countdown-expired">
        <Timer className="w-4 h-4" />
        <span>Draw time!</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5" data-testid="countdown-timer">
      <CountdownUnit value={timeLeft.days} label="days" />
      <span className="text-gray-400 font-bold text-sm mt-[-12px]">:</span>
      <CountdownUnit value={timeLeft.hours} label="hrs" />
      <span className="text-gray-400 font-bold text-sm mt-[-12px]">:</span>
      <CountdownUnit value={timeLeft.minutes} label="min" />
      <span className="text-gray-400 font-bold text-sm mt-[-12px]">:</span>
      <CountdownUnit value={timeLeft.seconds} label="sec" />
    </div>
  );
}

function calcTimeLeft(drawDate) {
  const diff = new Date(drawDate) - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    expired: false
  };
}

function RaffleCard({ raffle, index }) {
  const progress = (raffle.tickets_sold / raffle.total_tickets) * 100;
  const remaining = raffle.total_tickets - raffle.tickets_sold;
  const drawDate = new Date(raffle.draw_date);
  const imageUrl = raffle.image_url || RAFFLE_IMAGES[index % RAFFLE_IMAGES.length];

  return (
    <Link 
      to={`/raffle/${raffle.raffle_id}`}
      className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-[#EAB308]/50 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(234,179,8,0.15)] transition-all duration-300 group"
      data-testid={`raffle-card-${raffle.raffle_id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={imageUrl} 
          alt={raffle.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          {raffle.status === 'active' ? (
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
              Active
            </span>
          ) : raffle.status === 'drawn' ? (
            <span className="bg-[#EAB308] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
              Drawn
            </span>
          ) : (
            <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
              Closed
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-heading text-xl font-bold text-gray-900 mb-2 group-hover:text-[#EAB308] transition-colors">
          {raffle.title}
        </h3>
        
        <div className="flex items-center gap-2 text-[#EAB308] text-2xl font-bold mb-4">
          <span>£{raffle.prize_amount.toLocaleString()}</span>
        </div>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-500">{raffle.tickets_sold} / {raffle.total_tickets} sold</span>
            <span className="text-[#EAB308] font-medium">{remaining} left</span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-100" />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          {raffle.status === 'active' ? (
            <Countdown drawDate={raffle.draw_date} />
          ) : (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>{drawDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          
          <div className="flex items-center gap-1 text-[#EAB308] font-medium group-hover:gap-2 transition-all">
            <span>£{raffle.ticket_price}</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { user, logout } = useAuth();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeRound, setActiveRound] = useState(null);
  const [winners, setWinners] = useState([]);
  const [freeEntry, setFreeEntry] = useState({ full_name: '', email: '', phone: '' });
  const [freeEntryMsg, setFreeEntryMsg] = useState('');
  const [freeEntryError, setFreeEntryError] = useState('');
  const [submittingFree, setSubmittingFree] = useState(false);
  const [freeEntryAgreed, setFreeEntryAgreed] = useState(false);
  const [paidEntry, setPaidEntry] = useState({ full_name: '', email: '', quantity: 1 });
  const [paidEntryAgreed, setPaidEntryAgreed] = useState(false);
  const [submittingPaid, setSubmittingPaid] = useState(false);
  const [paidEntryError, setPaidEntryError] = useState('');

  useEffect(() => {
    fetchRaffles();
    fetchRoundData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRaffles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/raffles`);
      setRaffles(response.data);
    } catch (error) {
      console.error('Error fetching raffles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoundData = async () => {
    try {
      const [roundRes, winnersRes] = await Promise.all([
        axios.get(`${API_URL}/api/rounds/active`),
        axios.get(`${API_URL}/api/rounds/winners`)
      ]);
      setActiveRound(roundRes.data);
      setWinners(winnersRes.data);
    } catch (error) {
      console.error('Error fetching round data:', error);
    }
  };

  const handleFreeEntry = async (e) => {
    e.preventDefault();
    setSubmittingFree(true);
    setFreeEntryMsg('');
    setFreeEntryError('');
    try {
      const res = await axios.post(`${API_URL}/api/rounds/free-entry`, freeEntry);
      setFreeEntryMsg(`Entry registered for Round #${res.data.round_number}! Entry #${res.data.entry.ticket_number}`);
      setFreeEntry({ full_name: '', email: '', phone: '' });
      fetchRoundData();
    } catch (err) {
      setFreeEntryError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmittingFree(false);
    }
  };

  const activeRaffles = raffles.filter(r => r.status === 'active');
  const pastRaffles = raffles.filter(r => r.status !== 'active');

  const handlePaidEntry = async (e) => {
    e.preventDefault();
    setSubmittingPaid(true);
    setPaidEntryError('');
    try {
      const res = await axios.post(`${API_URL}/api/rounds/paid-checkout`, {
        full_name: paidEntry.full_name,
        email: paidEntry.email,
        quantity: paidEntry.quantity,
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setPaidEntryError(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setSubmittingPaid(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {user ? (
                <>
                  <Link to="/my-tickets" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-my-tickets">
                    <Ticket className="w-5 h-5" />
                    My Tickets
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-admin">
                      <LayoutDashboard className="w-5 h-5" />
                      Admin
                    </Link>
                  )}
                  <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                    <span className="text-gray-600 text-sm">{user.full_name || user.email}</span>
                    <button
                      onClick={logout}
                      className="text-gray-600 hover:text-red-500 transition-colors"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                    data-testid="nav-login"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-[#EAB308] text-white font-bold px-4 py-2 rounded-xl hover:bg-[#CA8A04] transition-colors"
                    data-testid="nav-register"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-3">
              {user ? (
                <>
                  <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                    <User className="w-8 h-8 text-[#EAB308]" />
                    <div>
                      <p className="text-gray-900 font-medium">{user.full_name || 'User'}</p>
                      <p className="text-gray-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                  <Link 
                    to="/my-tickets" 
                    className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Ticket className="w-5 h-5" />
                    My Tickets
                  </Link>
                  {user.role === 'admin' && (
                    <Link 
                      to="/admin" 
                      className="flex items-center gap-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 py-2 text-red-500 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="block py-2 text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register" 
                    className="block bg-[#EAB308] text-white font-bold px-4 py-3 rounded-xl text-center hover:bg-[#CA8A04] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32 min-h-[500px] flex items-center">
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1720299580598-b379d782e785?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjBwcml6ZSUyMHRyb3BoeSUyMHdpbm5lciUyMGNlbGVicmF0aW9uJTIwY29uZmV0dGl8ZW58MHx8fHwxNzc1NjUyNDQ5fDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(4px)',
            transform: 'scale(1.1)'
          }}
        />
        {/* Golden Dark Overlay for text readability */}
        <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(135deg, rgba(120, 80, 0, 0.75) 0%, rgba(60, 40, 0, 0.85) 100%)' }} />
        
        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-tighter mb-6 drop-shadow-lg">
            Win Big with <span className="text-[#FFD700]">Boss Win</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10 drop-shadow-md">
            Your chance to win incredible prizes. Fast, simple, and transparent free draws with guaranteed winners.
          </p>
          {!user && (
            <Link 
              to="/register"
              className="inline-flex items-center gap-2 bg-[#FFD700] text-[#3D2800] font-bold px-8 py-4 rounded-xl hover:bg-[#FFC000] transition-all text-lg shadow-xl hover:shadow-2xl hover:scale-105"
              data-testid="hero-cta"
            >
              <Trophy className="w-5 h-5" />
              Start Winning Now
            </Link>
          )}
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">

        {/* Current Round Banner */}
        {activeRound && (
          <section className="mb-12 -mt-12 relative z-30" data-testid="active-round-section">
            <div className="bg-gray-900 rounded-2xl p-6 md:p-8 shadow-2xl border border-gray-800">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-500 w-2 h-2 rounded-full animate-pulse" />
                    <span className="text-green-400 text-sm font-medium uppercase tracking-wider">Live Now</span>
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-black text-white mb-1">
                    Free Draw — Round #{activeRound.round_number}
                  </h2>
                  <p className="text-gray-400">
                    {activeRound.total_entries} {activeRound.total_entries === 1 ? 'entry' : 'entries'} so far
                  </p>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-gray-400 text-sm mb-2">Closes in</p>
                  <Countdown drawDate={activeRound.close_date} />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Entry Section - Free & Paid with Equal Prominence */}
        {activeRound && (
          <section className="mb-16" data-testid="entry-section">
            {/* Eligibility Notice */}
            <p className="text-center text-gray-500 text-sm mb-4" data-testid="eligibility-notice">
              Open to UK residents aged 18+. Free and paid entries carry equal weight.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Free Entry */}
              <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-[#EAB308]/20 rounded-2xl p-6" data-testid="free-entry-section">
                <div className="text-center mb-5">
                  <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">No Purchase Necessary</span>
                  <h3 className="font-heading text-xl font-bold text-gray-900">Free Entry</h3>
                  <p className="text-gray-500 text-sm mt-1">One free entry per person per round</p>
                </div>
                <form onSubmit={handleFreeEntry} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    required
                    value={freeEntry.full_name}
                    onChange={e => setFreeEntry(p => ({...p, full_name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#EAB308] focus:border-transparent outline-none text-sm"
                    data-testid="free-entry-name"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    value={freeEntry.email}
                    onChange={e => setFreeEntry(p => ({...p, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#EAB308] focus:border-transparent outline-none text-sm"
                    data-testid="free-entry-email"
                  />
                  <AgeTermsCheckbox checked={freeEntryAgreed} onChange={setFreeEntryAgreed} />
                  <button
                    type="submit"
                    disabled={submittingFree || !freeEntryAgreed}
                    className="w-full bg-[#EAB308] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="free-entry-submit"
                  >
                    {submittingFree ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                    Enter Free
                  </button>
                </form>
                {freeEntryMsg && (
                  <p className="mt-3 text-green-600 text-sm text-center font-medium" data-testid="free-entry-success">{freeEntryMsg}</p>
                )}
                {freeEntryError && (
                  <p className="mt-3 text-red-500 text-sm text-center" data-testid="free-entry-error">{freeEntryError}</p>
                )}
              </div>

              {/* Paid Entry */}
              <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-[#EAB308]/20 rounded-2xl p-6" data-testid="paid-entry-section">
                <div className="text-center mb-5">
                  <span className="inline-block bg-[#EAB308]/10 text-[#B8860B] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">Support & Enter</span>
                  <h3 className="font-heading text-xl font-bold text-gray-900">Paid Entry — £1.00 each</h3>
                  <p className="text-gray-500 text-sm mt-1">Buy multiple entries for more chances</p>
                </div>
                <form onSubmit={handlePaidEntry} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    required
                    value={paidEntry.full_name}
                    onChange={e => setPaidEntry(p => ({...p, full_name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#EAB308] focus:border-transparent outline-none text-sm"
                    data-testid="paid-entry-name"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    value={paidEntry.email}
                    onChange={e => setPaidEntry(p => ({...p, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#EAB308] focus:border-transparent outline-none text-sm"
                    data-testid="paid-entry-email"
                  />
                  <div className="flex items-center justify-center gap-4 py-2">
                    <button
                      type="button"
                      onClick={() => setPaidEntry(p => ({...p, quantity: Math.max(1, p.quantity - 1)}))}
                      className="w-10 h-10 rounded-xl border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                      data-testid="paid-qty-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <div className="text-center" data-testid="paid-qty-display">
                      <span className="text-2xl font-bold text-gray-900">{paidEntry.quantity}</span>
                      <p className="text-xs text-gray-500">{paidEntry.quantity === 1 ? 'entry' : 'entries'} — £{(paidEntry.quantity * 1).toFixed(2)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPaidEntry(p => ({...p, quantity: Math.min(10, p.quantity + 1)}))}
                      className="w-10 h-10 rounded-xl border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                      data-testid="paid-qty-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <AgeTermsCheckbox checked={paidEntryAgreed} onChange={setPaidEntryAgreed} />
                  <button
                    type="submit"
                    disabled={submittingPaid || !paidEntryAgreed}
                    className="w-full bg-gray-900 text-white font-bold px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="paid-entry-submit"
                  >
                    {submittingPaid ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Buy {paidEntry.quantity} {paidEntry.quantity === 1 ? 'Entry' : 'Entries'} — £{(paidEntry.quantity * 1).toFixed(2)}
                  </button>
                </form>
                {paidEntryError && (
                  <p className="mt-3 text-red-500 text-sm text-center" data-testid="paid-entry-error">{paidEntryError}</p>
                )}
              </div>
            </div>
          </section>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
          </div>
        ) : (
          <>
            {/* Active Raffles */}
            {activeRaffles.length > 0 && (
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-2 h-8 bg-[#EAB308] rounded-full" />
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Active Free Draws</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="active-raffles-grid">
                  {activeRaffles.map((raffle, index) => (
                    <RaffleCard key={raffle.raffle_id} raffle={raffle} index={index} />
                  ))}
                </div>
              </section>
            )}

            {/* Past Raffles */}
            {pastRaffles.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-2 h-8 bg-gray-400 rounded-full" />
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Past Free Draws</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="past-raffles-grid">
                  {pastRaffles.map((raffle, index) => (
                    <RaffleCard key={raffle.raffle_id} raffle={raffle} index={index + activeRaffles.length} />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {raffles.length === 0 && (
              <div className="text-center py-20">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">No free draws yet</h3>
                <p className="text-gray-500">Check back soon for exciting new free draws!</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Previous Winners */}
      {winners.length > 0 && (
        <section className="bg-gray-50 border-t border-gray-200 py-16" data-testid="winners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-2 h-8 bg-[#EAB308] rounded-full" />
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Previous Winners</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map(w => (
                <div key={w.round_number} className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4">
                  <div className="bg-[#EAB308]/10 rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-6 h-6 text-[#EAB308]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-900 font-bold truncate">Round #{w.round_number}</p>
                    <p className="text-[#EAB308] font-medium truncate">{w.display_name}</p>
                    <p className="text-gray-400 text-xs">
                      {w.drawn_at ? new Date(w.drawn_at).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'}) : ''}
                      {w.prize_claimed && <span className="ml-2 text-green-500">Claimed</span>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <LegalFooter />
    </div>
  );
}
